'use client';
import { Globe, Lock, X } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { analytics } from '@/lib/analytics';
import { Button, LinkButton } from '../Button/Button';
import { ToneAndChildren, toTonePathname } from '@/utils/helpers';

interface PrivateWarningProps {
  tone: ToneAndChildren;
  type: 'banner' | 'icon';
}

export const DumbyBanner = () => {
  return (
    <div className="bg-zinc-900 w-full text-white p-2 flex items-center justify-center gap-4 mt-[-1px] group">
      <div className="text-sm">
        <Lock className="w-6 h-6 text-zinc-400 group-hover" />
      </div>
      <div className="text-base font-semibold">Private</div>
    </div>
  );
};

const PrivateWarning = ({ tone, type = 'banner' }: PrivateWarningProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const isPublic = tone.is_public;

  const toggleModal = () => {
    setIsOpen(!isOpen);
    if (isOpen) {
      analytics.capture('tone.change_visibility_modal.opened', {
        toneId: tone.id,
      });
    }
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        toggleModal();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const renderBanner = () => {
    return (
      <button
        className="bg-zinc-900 w-full text-white p-2 flex items-center justify-center gap-4 mt-[-1px] group"
        onClick={toggleModal}
      >
        <div className="text-sm">
          <Lock className="w-6 h-6 text-zinc-400 group-hover" />
        </div>
        <div className="text-base font-semibold">Private</div>
      </button>
    );
  };

  const renderIcon = () => {
    return (
      <button onClick={toggleModal} className="group">
        {isPublic ? (
          <Globe className="w-6 h-6 text-white" />
        ) : (
          <Lock className="w-6 h-6 text-zinc-400" />
        )}
      </button>
    );
  };

  return (
    <>
      {type === 'banner' && renderBanner()}
      {type === 'icon' && renderIcon()}
      {isOpen && (
        <span className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-40">
          <span
            ref={modalRef}
            className="bg-zinc-900 border border-zinc-500 border-opacity-50 text-white w-full max-w-sm rounded-xl shadow-xl flex flex-col"
          >
            <span className="flex justify-between items-center p-4 border-zinc-700">
              <span className="flex flex-row items-center gap-4">
                <span className="text-xl font-semibold">Change visibility</span>
              </span>
              <button onClick={toggleModal} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </span>
            <span className="p-4 flex flex-col gap-6">
              <span className="text-zinc-400 text-left">
                Edit your tone to make it public or private.
              </span>
              <span className={'flex flex-row gap-4'}>
                {/* <Button
                  className={'w-full'}
                  onClick={toggleModal}
                  isSecondary={true}
                  variant={'slim'}
                >
                  {`Keep ${isPublic ? 'Public' : 'Private'}`}
                </Button> */}
                <LinkButton
                  href={`/tones/${toTonePathname(tone)}/edit`}
                  className={'w-full'}
                  isPrimary={true}
                  variant={'slim'}
                >
                  Edit Tone
                </LinkButton>
              </span>
            </span>
          </span>
        </span>
      )}
    </>
  );
};

export default PrivateWarning;
